import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Clock, FileText, Camera, User } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface NavItem {
  nameKey: 'navHome' | 'navAttendance' | 'navRequests' | 'navProfile';
  href: string;
  icon: React.ElementType;
}

const leftItems: NavItem[] = [
  { nameKey: 'navHome', href: '/', icon: Home },
  { nameKey: 'navAttendance', href: '/attendance', icon: Clock },
];

const rightItems: NavItem[] = [
  { nameKey: 'navRequests', href: '/requests', icon: FileText },
  { nameKey: 'navProfile', href: '/profile', icon: User },
];

interface BottomNavProps {
  onCameraFab: () => void;
}

export default function BottomNav({ onCameraFab }: BottomNavProps): React.JSX.Element {
  const location = useLocation();
  const { t } = useLanguage();

  const isActive = (href: string): boolean =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  return (
    <nav
      className="bottom-nav"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="bottom-nav-inner">
        {/* ── Left items ── */}
        {leftItems.map((item) => {
          const active = isActive(item.href);
          return (
            <Link
              key={item.nameKey}
              to={item.href}
              className={`bottom-nav-item ${active ? 'bottom-nav-item--active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <span className="bottom-nav-icon">
                <item.icon strokeWidth={active ? 2.5 : 1.75} />
              </span>
              <span className="bottom-nav-label">{t[item.nameKey]}</span>
            </Link>
          );
        })}

        {/* ── FAB – Camera Attendance shortcut (floating center) ── */}
        <div className="bottom-nav-fab-wrapper">
          <button
            type="button"
            onClick={onCameraFab}
            className="bottom-nav-fab"
            aria-label="Open attendance camera"
          >
            <Camera strokeWidth={2} className="bottom-nav-fab-icon" />
          </button>
        </div>

        {/* ── Right items ── */}
        {rightItems.map((item) => {
          const active = isActive(item.href);
          return (
            <Link
              key={item.nameKey}
              to={item.href}
              className={`bottom-nav-item ${active ? 'bottom-nav-item--active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <span className="bottom-nav-icon">
                <item.icon strokeWidth={active ? 2.5 : 1.75} />
              </span>
              <span className="bottom-nav-label">{t[item.nameKey]}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
